// useNavGroups — the permission-gated navigation model for the current operator (task 355.4).
// Shared by the sidebar and the command palette; the pending-task query key is shared with the bell.
// Task 359: also loads the chart of accounts (same cache as the Accounts page) for the Accounts sections.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../stores/authStore';
import { assignmentKeys, getMyAssignmentCount } from '../../../api/endpoints/documentAccess';
import useDocumentTypeTree from '../../hooks/useDocumentTypeTree';
import { buildNavGroups, paymentsShortcut, type NavGroup, type PaymentsShortcut } from './navModel';

/** Task 402.9: the badge counts the restricted documents assigned to the operator (the old workflow task queue was never written to). */
export function usePendingTaskCount(): number {
  const { data } = useQuery({
    queryKey: assignmentKeys.myCount(),
    queryFn: getMyAssignmentCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return data ?? 0;
}

export function useNavGroups(): {
  groups: NavGroup[];
  documentTreeLoading: boolean;
  /** Task 381: which document types the Payments shortcut covers, for highlighting. */
  payments: PaymentsShortcut | null;
} {
  // `permissions`/`roles` are listed so the model rebuilds when /me resolves: the has* functions are
  // stable store references and would not change on their own.
  const { hasPermission, hasRole, permissions, roles } = useAuth();
  const { data: documentTree, isLoading: documentTreeLoading } = useDocumentTypeTree();
  const pendingTaskCount = usePendingTaskCount();

  const groups = useMemo(
    () => buildNavGroups({ hasRole, hasPermission, documentTree, pendingTaskCount }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasRole, hasPermission, permissions, roles, documentTree, pendingTaskCount],
  );

  const payments = useMemo(() => paymentsShortcut(documentTree), [documentTree]);

  return { groups, documentTreeLoading, payments };
}
